import { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {
  menuItems,
  MenuItem,
  getCommandPaletteDisplayLabel,
  menuItemMatchesSearchQuery,
} from '../../constants/menuItems'
import { SidebarContext } from '../../contexts/SidebarContext'
import { useCommandPaletteActions, CommandPaletteActions } from '../../contexts/CommandPaletteContext'
import { useAppSelector, useAppDispatch } from '../../hooks'
import { fetchCustomerAccountAction } from '../../store/customerAccounts/actions'

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  userData?: any
  actions?: CommandPaletteActions
}


function highlightText(text: string, query: string): React.ReactNode {
  if (!query) return text
  
  const textLower = text.toLowerCase()
  const queryLower = query.toLowerCase()
  const index = textLower.indexOf(queryLower)
  
  if (index === -1) return text
  
  const before = text.substring(0, index)
  const match = text.substring(index, index + query.length)
  const after = text.substring(index + query.length)
  
  return (
    <>
      {before}
      <Box component="span" sx={{ fontWeight: 700, backgroundColor: alpha('#1976d2', 0.2) }}>
        {match}
      </Box>
      {after}
    </>
  )
}

const RECENT_MENU_ITEMS_KEY = 'commandPaletteRecentItems'
const MAX_RECENT_ITEMS = 4

interface SiteResult {
  id: string
  siteId: number
  customerId: number
  label: string
  siteAddress: string
  customerName: string
}

export default function CommandPalette({ open, onClose, userData, actions: propsActions }: CommandPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([])
  const [filteredSites, setFilteredSites] = useState<SiteResult[]>([])
  const [recentItems, setRecentItems] = useState<MenuItem[]>([])
  const navigate = useNavigate()
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const { closeSidebar } = useContext(SidebarContext)
  const contextActions = useCommandPaletteActions()
  const actions = propsActions || contextActions
  const dispatch = useAppDispatch()
  
  // Get sites data from Redux
  const sitesData = useAppSelector((state) => state.manageCustomerAccounts.list.data)
  const sitesLoading = useAppSelector((state) => state.manageCustomerAccounts.list.loading)
  const hasFetchedRef = useRef(false)

  // Fetch sites data when command palette opens if data is empty (e.g., new tab scenario)
  // Use ref to prevent infinite loops - only fetch once per command palette session
  useEffect(() => {
    // Reset fetch flag when command palette closes
    if (!open) {
      hasFetchedRef.current = false
      return
    }

    // Only fetch if:
    // 1. Command palette is open
    // 2. No sites data exists
    // 3. Not currently loading
    // 4. Haven't already attempted to fetch in this session
    if (open && !sitesData?.length && !sitesLoading && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      dispatch(fetchCustomerAccountAction('OPEN'))
    }
  }, [open, sitesData?.length, sitesLoading, dispatch])

  // Load recent items from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_MENU_ITEMS_KEY)
      if (stored) {
        const recentIds = JSON.parse(stored) as string[]
        const recent = recentIds
          .map((id) => menuItems.find((item) => item.id === id))
          .filter((item): item is MenuItem => item !== undefined)
        setRecentItems(recent)
      }
    } catch (error) {
      // Ignore errors
    }
  }, [])

  // Search sites function
  const searchSites = useCallback((query: string, sites: any[]): SiteResult[] => {
    if (!sites?.length || !query.trim()) return []
    
    const queryLower = query.toLowerCase()
    return sites
      .filter((account) => {
        // Data structure: account has id (siteId), siteAddress, customer: { id, name, mobile, address }
        const { id, siteAddress, customer } = account
        return (
          `${id}`.toLowerCase().includes(queryLower) ||
          siteAddress?.toLowerCase().includes(queryLower) ||
          customer?.name?.toLowerCase().includes(queryLower) ||
          customer?.mobile?.toLowerCase().includes(queryLower)
        )
      })
      .slice(0, 3)
      .map((account) => ({
        id: `site-${account.id}`,
        siteId: account.id,
        customerId: account.customer?.id,
        label: `${account.id} - ${account.siteAddress || account.customer?.name || ''}`,
        siteAddress: account.siteAddress,
        customerName: account.customer?.name,
      }))
  }, [])

  // Save recent item to localStorage
  const saveRecentItem = useCallback((item: MenuItem) => {
    try {
      const stored = localStorage.getItem(RECENT_MENU_ITEMS_KEY)
      const recentIds = stored ? (JSON.parse(stored) as string[]) : []
      
      // Remove if already exists and add to front
      const filtered = recentIds.filter((id) => id !== item.id)
      const updated = [item.id, ...filtered].slice(0, MAX_RECENT_ITEMS)
      
      localStorage.setItem(RECENT_MENU_ITEMS_KEY, JSON.stringify(updated))
      
      // Update state
      const recent = updated
        .map((id) => menuItems.find((menuItem) => menuItem.id === id))
        .filter((menuItem): menuItem is MenuItem => menuItem !== undefined)
      setRecentItems(recent)
    } catch (error) {
      // Ignore errors
    }
  }, [])

  // Filter menu items based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      // Show no results by default - user must type to see results
      setFilteredItems([])
      setFilteredSites([])
      return
    }

    const query = searchQuery.toLowerCase()
    const filtered = menuItems.filter((item) => {
      // Filter out Switch Account if user doesn't have multiple stores
      if (item.id === 'switch-account' && (!userData?.myStores || userData.myStores.length === 0)) {
        return false
      }
      
      // Filter out offline-only items if not in offline mode
      if ((item.id === 'purchase-activate-plan' || item.id === 'manage-backups') && !process.env.OFFLINE) {
        return false
      }

      return menuItemMatchesSearchQuery(item, query)
    })

    // Limit to 3 results max
    const menuResults = filtered.slice(0, 3)
    setFilteredItems(menuResults)
    
    // If no menu results, search sites
    if (menuResults.length === 0 && sitesData && sitesData.length > 0) {
      const siteResults = searchSites(searchQuery, sitesData)
      setFilteredSites(siteResults)
    } else {
      setFilteredSites([])
    }
    
    setSelectedIndex(0)
  }, [searchQuery, userData, sitesData, searchSites])

  const handleSelectItem = useCallback(
    (item: MenuItem) => {
      closeSidebar()
      
      // Save to recent items
      saveRecentItem(item)
      
      // Close immediately to prevent any event propagation
      onClose()

      // Use setTimeout to ensure command palette is closed before opening dialog
      setTimeout(() => {
        if (item.type === 'navigation' && item.route) {
          navigate(item.route())
        } else if (item.type === 'action' && item.actionId) {
          // Map actionId to handler function
          const actionMap: Record<string, () => void> = {
            openNewSite: actions.openNewSite || (() => {}),
            showAddCustomer: actions.showAddCustomer || (() => {}),
            showAddStock: actions.showAddStock || (() => {}),
            createOutwardChallan: actions.createOutwardChallan || (() => {}),
            createInwardChallan: actions.createInwardChallan || (() => {}),
            receiveLost: actions.receiveLost || (() => {}),
            showAddPayment: actions.showAddPayment || (() => {}),
            showOtherCharge: actions.showOtherCharge || (() => {}),
            showCreateMonthlyBill: actions.showCreateMonthlyBill || (() => {}),
            showCreateSaleInvoice: actions.showCreateSaleInvoice || (() => {}),
            siteTransfer: actions.siteTransfer || (() => {}),
            showReceiveExcess: actions.showReceiveExcess || (() => {}),
          }
          const handler = actionMap[item.actionId]
          if (handler) {
            handler()
          }
        } else if (item.type === 'logout' && actions.handleLogout) {
          actions.handleLogout()
        }
      }, 150)
    },
    [navigate, onClose, actions, closeSidebar, saveRecentItem]
  )

  const handleSelectSite = useCallback(
    (site: SiteResult) => {
      closeSidebar()
      onClose()
      setTimeout(() => {
        navigate(`/slips/view-slips/${site.siteId}/${site.customerId}`)
      }, 150)
    },
    [navigate, onClose, closeSidebar]
  )

  const handleSelect = useCallback(
    (index: number) => {
      if (index < filteredItems.length) {
        handleSelectItem(filteredItems[index])
      } else {
        const siteIndex = index - filteredItems.length
        if (filteredSites[siteIndex]) {
          handleSelectSite(filteredSites[siteIndex])
        }
      }
    },
    [filteredItems, filteredSites, handleSelectItem, handleSelectSite]
  )

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return

    const totalResults = filteredItems.length + filteredSites.length
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        setSelectedIndex((prev) => Math.min(prev + 1, Math.max(0, totalResults - 1)))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        // Create a synthetic event to mark it as handled
        Object.defineProperty(e, 'defaultPrevented', { value: true, writable: false })
        if (totalResults > 0) {
          // Use setTimeout to ensure event is fully processed
          setTimeout(() => {
            handleSelect(selectedIndex)
          }, 0)
        }
        return false
      } else if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    // Use capture phase to catch event early
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, selectedIndex, filteredItems, filteredSites, onClose, handleSelect])

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`)
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedIndex])

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      // Use multiple attempts to ensure focus works
      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus()
          inputRef.current.select()
          return true
        }
        return false
      }
      
      // Try immediately
      if (!focusInput()) {
        // Try after a short delay
        setTimeout(() => {
          if (!focusInput()) {
            // Try after animation frame
            requestAnimationFrame(() => {
              setTimeout(() => {
                focusInput()
              }, 100)
            })
          }
        }, 50)
      }
    } else {
      setSearchQuery('')
      setSelectedIndex(0)
      setFilteredSites([])
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disableAutoFocus
      disableEnforceFocus
      onKeyDown={(e) => {
        // Prevent Enter key from propagating when dialog is open
        if (e.key === 'Enter' && (filteredItems.length > 0 || filteredSites.length > 0)) {
          e.stopPropagation()
          e.preventDefault()
        }
      }}
      PaperProps={{
        sx: {
          mt: '100px',
          width: '600px',
          height: 'auto',
          minHeight: '220px',
          maxHeight: '220px',
          position: 'fixed',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        },
      }}
      TransitionProps={{
        onEntered: () => {
          if (inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
          }
        },
      }}
    >
      <DialogContent 
        sx={{ 
          p: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
        }}
      >
        <Box 
          sx={{ 
            p: 2.5, 
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, 
            flexShrink: 0,
            backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#fafafa',
          }}
        >
          <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            placeholder="Search sidebar menu or sites (eg: add party, site 123)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.95rem',
                borderRadius: '12px',
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#2f2f2f' : '#f9f9f9',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#2f2f2f' : '#ffffff',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
                '& fieldset': {
                  borderColor: alpha(theme.palette.divider, 0.2),
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px',
                },
              },
            }}
          />
        </Box>

        {/* Recent Items */}
        {!searchQuery.trim() && recentItems.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#fafafa',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
                display: 'block',
              }}
            >
              Recent
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row', overflowX: 'auto', flexWrap: 'nowrap' }}>
              {recentItems.slice(0, 4).map((item) => (
                <Box
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: 'text.primary',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box 
          ref={listRef}
          sx={{
            flex: 1,
            overflow: 'hidden',
            minHeight: '140px',
            maxHeight: '140px',
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
          }}
        >
          {filteredItems.length === 0 && filteredSites.length === 0 ? (
            <Box 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}
            >
              <Typography 
                color="text.secondary"
                sx={{
                  fontSize: '0.9rem',
                  opacity: 0.6,
                }}
              >
                No results found
              </Typography>
            </Box>
          ) : (
            <List 
              dense 
              sx={{ 
                height: '100%', 
                overflow: 'hidden',
                py: 0.5,
              }}
            >
              {filteredItems.map((item, index) => {
                const isSelected = index === selectedIndex
                const displayLabel = getCommandPaletteDisplayLabel(item)

                return (
                  <ListItem
                    key={item.id}
                    disablePadding
                    data-index={index}
                    onClick={() => handleSelectItem(item)}
                    sx={{
                      px: 1.5,
                      mb: 0.5,
                    }}
                  >
                    <ListItemButton
                      selected={isSelected}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.action.hover, 0.5),
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="span">
                              {highlightText(displayLabel, searchQuery)}
                            </Box>
                            {item.keyboardShortcut && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  fontFamily: 'monospace',
                                  color: 'text.secondary',
                                  ml: 'auto',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {item.keyboardShortcut}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
              {filteredSites.map((site, index) => {
                const itemIndex = filteredItems.length + index
                const isSelected = itemIndex === selectedIndex

                return (
                  <ListItem
                    key={site.id}
                    disablePadding
                    data-index={itemIndex}
                    onClick={() => handleSelectSite(site)}
                    sx={{
                      px: 1.5,
                      mb: 0.5,
                    }}
                  >
                    <ListItemButton
                      selected={isSelected}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.action.hover, 0.5),
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="span">
                              {highlightText(site.label, searchQuery)}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

